/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { LabelType } from '@airasia-phoenix/react-native/components/LabelView/types';
import {
  getColor,
  getTextStyle,
  pd,
} from '@airasia-phoenix/react-native/utils/utils';

import { AdditionalLabels } from '../../config/constants';
import AsyncStorageOriginDestinationItem from '../../types/recentSearchesAsyncStorage.types';
import { getStringLength, insertParamsInUrl } from '../../util/utils';
import Constants from './constants';
import {
  FlightsMemoisedListViewProps,
  FlightsSectionListItem,
  StationsSearchResponse,
} from './types';
import { StationDataObject } from '../../types/FlightSearch.types';
import { OriginWithDestinations } from '../../hooks/useRecentSearches';
import { STATIONS } from '../../config/constants';
import type { TextStyle } from 'react-native';
import { Icon } from '@airasia-phoenix/react-native/utils/utils';

/**
 *  ?Station Search Data Transformation steps-
 * 1. API Data (From Station Search API).
 * 2. MemoisedListViewProps[] (After conversion of API data , to support rendering of Searchable List Page) , done by
 * mapToListViewProps.
 * 3. If highlighting enabled then MemoisedListViewProps[] are further injected with spannable Props
 * to enable highlighting, done by mapToListViewPropsWithHighlighting.
 */

/**
 * ?Recent Searches Transformation steps-
 * 1. When Search Flights pressed, then the corresponding origin and destination station objects are stored
 * in object(of type AsyncStorageOriginDestinationItem).
 *
 * 2. To show them in the origin and destination fields the approach is different.
 *
 * 3. For origin -
 *  a. In all the AsyncStorageOriginDestinationItem items, filter out unique origins , done by getRecentSearchesForField.
 *  b. Then these station object array are like we get from Station Search API so just use mapToListViewProps.
 *  c. But these are to be displayed with header of Recent Searches so additionally mapToSectionListItem is used
 *  to what is called section item which contains the header (Recent Searches string) and the list of items we get from mapToListViewProps.
 *
 * 4. For destination - This is shown only when some origin is selected.
 *  a. In all the AsyncStorageOriginDestinationItem items, filter out the items that have origin as selected station.
 *  b. Now of these filtered AsyncStorageOriginDestinationItem items, map out the destination stations.
 *  c. Now this is again similar to station data array we get from Station Search API so just use mapToListViewProps.
 *  d. But these are to be displayed with header of Recent Searches so additionally mapToSectionListItem is used
 *  to what is called section item which contains the header (Recent Searches string) and the list of items we get from mapToListViewProps.
 */

/**
 * The API response we get from station search contains data as city and all the airports in that city in
 * Stations key which is an array. Now to display that as items list we will want to flatten it so
 * flattening it.
 * eg -
 *  City:Kuala Lumpur
 *  Stations:[
 *  {AirportName:"..."},
 *  {AirportName:"..."}
 *  ...
 *  ]
 * @param {StationDataObject[]}data - Stations API Response
 * @returns - The flattened Data is returned where stations key is spread into main array
 */
export const flattenStationsData = (data: StationsSearchResponse) => {
  if (!data?.length) return [];

  return data.reduce<StationDataObject[]>((acc, item) => {
    if (!item) return acc;

    acc.push({ ...item, IsGroupedUnderCityNode: false });

    if (
      item.StationType === STATIONS.TYPE.CITY &&
      Array.isArray(item.Stations)
    ) {
      acc.push(...item.Stations);
    }

    return acc;
  }, []);
};

/**
 * Refer - Line 19 and 28 in this file.
 * @param {StationsSearchResponse} data - The array of stations from type sense response.
 * @param {boolean} areRecentSearches - if recent searches then we don't need to flatten station inside current station.
 *  @param {boolean} areNearestAirports - if nearest airports padding start will be 16 despite they being airports and not cities.
 * @returns {FlightsMemoisedListViewProps[]}  The FlightsMemoisedListViewProps that will be used to render items in SearchableListPage.
 */
export const mapToListViewProps = (
  data: StationsSearchResponse,
  allAirportsLabel: string,
  areRecentSearches: boolean = false,
  areNearestAirports: boolean = false,
): FlightsMemoisedListViewProps[] => {
  // if recent searches then we don't want to flatten as we don't show data stored in Stations key flattened in this case.
  const flattenedData: StationDataObject[] = areRecentSearches
    ? (data as unknown as StationDataObject[])
    : flattenStationsData(data);
  let idCounter = 0;

  return flattenedData.map((item) => {
    const isCity = item.StationType === Constants.CITY_STATION_TYPE;
    idCounter++;

    const isGroupedUnderCityNode = item.IsGroupedUnderCityNode;

    let primaryText: string = '';

    if (areRecentSearches) {
      switch (item.StationType) {
        case STATIONS.TYPE.CITY:
        case STATIONS.TYPE.AIRPORT:
        case STATIONS.TYPE.BUS:
          primaryText = `${item.CityName}, ${item.CountryName}`;
          break;

        case STATIONS.TYPE.FERRY:
          primaryText = `${item.StationName}, ${item.CountryName}`;
          break;

        default:
          primaryText = item.CityName;
      }
    } else if (isCity) {
      primaryText = `${item.CityName}, ${item.CountryName}`;
    } else {
      primaryText = item.AirportName ?? item.StationName ?? item.CityName;
    }

    let secondaryText: string = '';

    if (isCity) {
      secondaryText = allAirportsLabel;
    } else if (areNearestAirports) {
      secondaryText = item.CountryName;
    } else if (areRecentSearches) {
      secondaryText = item.AirportName ?? '';
    } else if (item.IsGroupedUnderCityNode === false) {
      secondaryText = item.StationName;
    }

    let paddingStart = 16;

    if (
      !areNearestAirports &&
      !areRecentSearches &&
      !isCity &&
      isGroupedUnderCityNode !== false
    ) {
      paddingStart = 45;
    }

    let startIcon: Icon = pd.icon.main.communication.location_on;

    if (item.StationType === STATIONS.TYPE.CITY) {
      startIcon = pd.icon.main.communication.location_on;
    } else if (item.StationType === STATIONS.TYPE.AIRPORT) {
      startIcon = pd.icon.main.device.airplanemode_active;
    } else if (item.StationType === STATIONS.TYPE.BUS) {
      startIcon = pd.icon.main.maps.directions_bus_filled;
    } else if (item.StationType === STATIONS.TYPE.FERRY) {
      startIcon = pd.icon.main.maps.directions_boat_filled;
    }

    return {
      id: idCounter.toString(),
      model: { value: item },
      primaryText,
      primaryTextNumberOfLines: 1,
      secondaryText,
      primaryTextStyle:
        isCity ||
        areNearestAirports ||
        areRecentSearches ||
        isGroupedUnderCityNode === false
          ? (getTextStyle(
              pd.sys.typescale.body_text_medium,
              pd.sys.colour.content_primary,
            ) as TextStyle)
          : (getTextStyle(
              pd.sys.typescale.sub_body_text_medium,
              pd.sys.colour.content_primary,
            ) as TextStyle),
      secondaryTextStyle: getTextStyle(
        pd.sys.typescale.sub_body_text,
        pd.sys.colour.content_secondary,
      ) as TextStyle,
      paddingStart,
      startElement: {
        iconViewProps: {
          icon: startIcon,
          height: 24,
          width: 24,
          tint: getColor(pd.sys.colour.content_primary),
        },
      },
      endElement:
        isCity || item.IsCity
          ? undefined
          : {
              labelViewProps: {
                text: item.StationCode,
                type: LabelType.TONED_GREY,
                textStyle: getTextStyle(
                  pd.sys.typescale.caption_text_medium,
                  pd.sys.colour.content_primary,
                ) as TextStyle,
              },
              alignSelf: 'flex-start',
            },
    };
  });
};

/**
 * Refer - Line 19 and 28 in this file.
 * @param {FlightsMemoisedListViewProps[]} memoizedListViewProps - The array we get from mapToListViewProps.
 * @param {string} searchStr - Search string , what user typed in the search field of searchable list page.
 * @returns {FlightsMemoisedListViewProps[]} - The spannable props are passed for each item to enable highlighting.
 */
export const mapToListViewPropsWithHighlighting = (
  memoizedListViewProps: FlightsMemoisedListViewProps[],
  searchStr: string,
): FlightsMemoisedListViewProps[] => {
  return memoizedListViewProps.map((listViewPropItem) => {
    const spannablePrimaryText = findLongestCommonSubString(
      listViewPropItem.primaryText,
      searchStr,
    );
    //!Enables highlighting for secondary text.
    // const spannableSecondaryText = findLongestCommonSubString(
    //   listViewPropItem.secondaryText ?? '',
    //   searchStr,
    // );

    //!Enables highlighting for label text.
    // const spannableLabelText = findLongestCommonSubString(
    //   listViewPropItem.endElement?.labelViewProps?.text ?? '',
    //   searchStr,
    // );
    return {
      ...listViewPropItem,
      primaryHighlightText: spannablePrimaryText
        ? [
            {
              spannable: spannablePrimaryText,
              style: getTextStyle(
                pd.sys.typescale.body_text_medium,
                pd.sys.colour.content_primary,
              ),
            },
          ]
        : undefined,
      //!Enables highlighting for secondary text.
      // secondaryHighlightText: spannableSecondaryText
      //   ? [
      //       {
      //         spannable: spannableSecondaryText,
      //         style: getTextStyle(
      //           pd.sys.typescale.body_text_medium,
      //           pd.sys.colour.content_primary,
      //         ),
      //       },
      //     ]
      //   : undefined,

      //!Enables highlighting for label text.
      //   endElement: {
      //     labelViewProps: {
      //       ...listViewPropItem.endElement?.labelViewProps,
      //       highlightText: [
      //         {
      //           spannable: spannableLabelText,
      //           style: getTextStyle(
      //             pd.sys.typescale.caption_text_medium,
      //             pd.sys.colour.content_primary,
      //           ),
      //         },
      //       ],
      //     },
      //   },
    };
  });
};

/**
 * Refer - Line 19 and 28 in this file.
 * @param {AsyncStorageOriginDestinationItem[]}recentSearches - The array of object containing recent search.
 * @param {boolean}isDestination - To extract recent searches for origin and destination both have different logic.
 * @param {StationDataObject | undefined}selectedOriginObject - Used when finding recent searches for destination.
 * @returns {FlightsMemoisedListViewProps[]} - Returns MemoisedListViewProps[] so can be rendered in Searchable List Page.
 */
export const getRecentSearchesForField = (
  recentSearches: AsyncStorageOriginDestinationItem[],
  allAirportsLabel: string,
  isDestination: boolean = false,
  selectedOriginObject: StationDataObject | undefined = undefined,
): FlightsMemoisedListViewProps[] => {
  if (isDestination && selectedOriginObject !== undefined) {
    //if destination then return the unique destination objects corresponding to selected origin
    //filter recent searches based on the selected origin object
    //then map to only return destination from recent search item
    return mapToListViewProps(
      recentSearches
        .filter((recentSearch) =>
          sameStationObject(
            recentSearch.originStationObject,
            selectedOriginObject,
          ),
        )
        .map((recentSearch) => recentSearch.destinationStationObject),
      allAirportsLabel,
      true,
    );
  } else {
    {
      //if not destination then just return origin objects from async storage without duplication
      return mapToListViewProps(
        recentSearches.map((item) => item.originStationObject),
        allAirportsLabel,
        true,
      ).filter((item, index, arr) => {
        return (
          arr.findIndex((arrItem) => {
            return sameStationObject(arrItem.model.value, item.model.value);
          }) === index
        );
      });
    }
  }
};

/**
 * Refer - Line 19 and 28 in this file.
 * @param {string}title - The string header to be used in section list item.
 * @param {MemoizedListViewProps[]}data - The list items under the header.
 * @returns {FlightsSectionListItem} - Returns the Section List Item that can be rendered by Searchable List Page.
 */
export const mapToSectionListItem = (
  title: string,
  data: FlightsMemoisedListViewProps[],
): FlightsSectionListItem[] | FlightsMemoisedListViewProps[] => {
  //if data in section then can show as section else return empty array (no section item to show)
  if (data.length > 0) return [{ title: title, data: data }];
  else return [];
};

/**
 * This checks if both the station objects are same or not, this is done by comparing various things like -
 * Are both of same type ?
 * If both city then check city code, city name and country name.
 * If both airport then check station code, airport name, city name and country name.
 * @param {StationDataObject} station1 - first station object.
 * @param {StationDataObject} station2 - second station object.
 * @returns {boolean} - checks if the station1 and station2 are same or not.
 */
export const sameStationObject = (
  station1: StationDataObject | undefined,
  station2: StationDataObject | undefined,
) => {
  if (!station1 && !station2) return true;
  else if (!station1 || !station2) return false;
  const isStation1City = station1.StationType === Constants.CITY_STATION_TYPE;
  const isStation2City = station2.StationType === Constants.CITY_STATION_TYPE;

  //both are not of same type , both are neither city nor station
  if (isStation1City !== isStation2City) return false;
  //both are same type
  else {
    if (isStation1City)
      //both cities , check city code, city name and country name
      return (
        station1.CityCode === station2.CityCode &&
        station1.CityName === station2.CityName &&
        station1.CountryName === station2.CountryName
      );
    else {
      //both airports ,check airport name,city name and country name
      return (
        station1.StationCode === station2.StationCode &&
        station1.AirportName === station2.AirportName &&
        station1.CityName === station2.CityName &&
        station1.CountryName === station2.CountryName
      );
    }
  }
};

/**
 * goal -> search string2 in string1.
 * Alternatively, we will search for all the substrings of string2 in string1.
 * Also the logic is fine tuned to-
 * if the string we are searching is found in start of the parent string then no matter its
 * length highlight it else if the search string is found in between then it should have minimum length 3.
 *
 * eg- str1 = "Kuala Lumpur" str2="lum "
 *     output = "Lum"
 * @param {string}str1 - parent string/ search space.
 * @param {string}str2 - child string/ string to be searched.
 * @returns {string} - Actual substring in parent string that matches child string.
 */
export const findLongestCommonSubString = (str1: string, str2: string) => {
  //matching should be typecase insensitive(toLowerCase) and remove white spaces at ends(trim).
  const lowerCaseParentString = str1.toLowerCase().trim();
  const lowerCaseSearchString = str2.toLowerCase().trim();

  if (lowerCaseSearchString.length === 0 || lowerCaseParentString.length === 0)
    //if empty string passed as search string
    return '';
  function getAllSubStrings(str: string) {
    const substrings = [];
    for (let i = 0; i <= str.length - 1; i++) {
      for (let j = str.length - 1; j >= i; j--) {
        substrings.push(str.substring(i, j + 1));
      }
    }
    return substrings;
  }

  //find substrings of lowercased search string and sort the substrings based on length
  const sortedSubstringsBasedOnLength = getAllSubStrings(
    lowerCaseSearchString,
  ).sort((str1, str2) => {
    return str2.length - str1.length;
  });

  //now the first string that is entirely present we will return (as its longest), its indices in the original string we return
  let longestSearchString = '';
  let isPresentAtStart = false;
  sortedSubstringsBasedOnLength.forEach((searchString) => {
    const idx = lowerCaseParentString.indexOf(searchString);
    if (
      idx !== -1 &&
      longestSearchString.length <= searchString.length &&
      !isPresentAtStart
    ) {
      longestSearchString = searchString;
      if (idx === 0) isPresentAtStart = true;
    }
  });
  return isPresentAtStart &&
    longestSearchString[0].toLowerCase() === str2[0].toLowerCase()
    ? longestSearchString
    : longestSearchString.length > 2
      ? longestSearchString
      : '';
};

/**
 * When the text field is half the screen width
 * (like origin and destination fields) then will need to truncate long display strings.
 * @param {string|undefined}str - The long string to truncate.
 * @returns {string} - Truncated string.
 */
export const truncateDisplayString = (str: string | undefined) => {
  if (!str) return str;
  const limit = Constants.HALF_WIDTH_TEXT_FIELD_CHARACTER_LIMIT;
  if (getStringLength(str) <= limit) return str;
  return str.substring(0, limit - 3) + '...';
};

/**
 * Flatten OriginWithDestinations[] -> AsyncStorageOriginDestinationItem[]
 * so existing utils keep working unchanged.
 */
export const flattenRecentSearches = (
  persisted?: OriginWithDestinations[],
): AsyncStorageOriginDestinationItem[] => {
  if (!persisted?.length) return [];

  const out: AsyncStorageOriginDestinationItem[] = [];
  for (const origin of persisted) {
    for (const dest of origin.destinations ?? []) {
      out.push({
        originStationObject: origin,
        destinationStationObject: dest,
      });
    }
  }
  return out;
};

export const isEmptyObject = (
  value: unknown,
): value is Record<string, never> => {
  if (!value || typeof value !== 'object') return true;
  return Object.keys(value).length === 0;
};

/**
 * Returns display name for a station:
 * - City → "CityName (All Airports)"
 * - Airport → "AirportName"
 */
export const getStationDisplayName = (
  station: StationDataObject | undefined | null,
  allAirportsLabel: string,
): string => {
  if (!station) return '';

  let displayName: string;

  if (station.StationType === STATIONS.TYPE.CITY) {
    displayName = station.CityName;
  } else {
    displayName = `${station.CityName || station.StationName} (${station.StationCode})`;
  }
  return displayName;
};

export const getNearestAirportsStationsSearchableList = (
  nearestAirportLabel: string,
  allAirportsLabel: string,
  nearestStations: StationsSearchResponse,
) => {
  return mapToSectionListItem(
    nearestAirportLabel,
    mapToListViewProps(nearestStations, allAirportsLabel, false, true),
  );
};

export const getRecentSearchesSearchableList = (
  recentSearchesLabel: string,
  allAirportsLabel: string,
  recentSearches: OriginWithDestinations[] = [],
) => {
  const originItems = mapToListViewProps(
    recentSearches,
    allAirportsLabel,
    true, // areRecentSearches=true
  ).filter((item, index, arr) => {
    return (
      arr.findIndex((arrItem) =>
        sameStationObject(arrItem.model.value, item.model.value),
      ) === index
    );
  });

  return mapToSectionListItem(recentSearchesLabel, originItems);
};

export const getStationsSearchableList = (
  stationData: StationsSearchResponse,
  allAirportsLabel: string,
  searchText: string,
) => {
  return mapToListViewPropsWithHighlighting(
    mapToListViewProps(stationData, allAirportsLabel),
    '',
  );
};

const getKey = (s: StationDataObject) =>
  `${s.IsCity ? '1' : '0'}|${s.CityCode}|${s.StationCode}`;

export const getDestinationsForOrigin = (
  origin: StationDataObject | null | undefined,
  recentSearches?: OriginWithDestinations[],
): StationDataObject[] => {
  if (!origin || !recentSearches?.length) return [];

  const originKey = getKey(origin);

  return (
    recentSearches.find((o) => getKey(o) === originKey)?.destinations ?? []
  );
};

export const getRecentDestinationsSearchableList = (
  label: string,
  allAirportsLabel: string,
  destinations: StationDataObject[] = [],
) => {
  const items = mapToListViewProps(
    destinations,
    allAirportsLabel,
    true, // areRecentSearches=true (or rename flag if needed)
  ).filter((item, index, arr) => {
    return (
      arr.findIndex((arrItem) =>
        sameStationObject(arrItem.model.value, item.model.value),
      ) === index
    );
  });

  return mapToSectionListItem(label, items);
};
